import {
    Button,
    Checkbox,
    DatePicker,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Select,
    Space,
    Tabs
} from "antd";
import type { Project, ProjectExtend, PurchaseGoodsReceiptVo } from "@zhihaoscm/entity";
import {
    PERMISSION,
    PROJECT_STATE,
    PURCHASE_RECONCILIATION_INVOICE_STATE,
    PURCHASE_RECONCILIATION_RECONCILIATION_STATE,
    PURCHASE_TYPE,
    ROLE_CODE
} from "@zhihaoscm/meta";
import { dateFormat, dateTimeFormat, formatNumber } from "@zhihaoscm/utils";
import dayjs from "dayjs";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { projectService } from "@/api/projectService";
import { purchaseGoodsReceiptService } from "@/api/purchaseGoodsReceiptService";
import { purchaseReconciliationService } from "@/api/purchaseReconciliationService";
import {
    Breadcrumb,
    DetailButtons,
    DetailHeader,
    EmptyState,
    InfoCard,
    ProTable,
    ZhBaseItem,
    ZhGrid,
    ZhMessageBox,
    type DetailAction,
    type ProColumn
} from "@/components/common";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";
import {
    INVOICE_STATE,
    RECONCILIATION_QUERY_TYPE,
    RECONCILIATION_STATE
} from "../../../../../common/enum/reconciliation";
import { SIGN_STATE } from "../../../../../common/enum/purchaseGoodsReceipt";
import type {
    PurchaseReconciliation,
    PurchaseReconciliationForm,
    PurchaseReconciliationVo
} from "../../../../../common/types/purchaseReconciliation";
import "./console.css";

const reconciliationStateOptions = PURCHASE_RECONCILIATION_RECONCILIATION_STATE.options.map(item => ({
    label: item.label,
    value: Number(item.value)
}));
/** 对账列表保留供货方/对账方双视角以及旧版筛选字段。 */
export function ReconciliationListPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const userInfo = useUserSession(state => state.userInfo);
    const account = useAccountSession();
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const routeType = Number(searchParams.get("type"));
    const [type, setType] = useState(
        isCustomer
            ? RECONCILIATION_QUERY_TYPE.TWO.code
            : isSupplier
              ? RECONCILIATION_QUERY_TYPE.ONE.code
              : routeType === RECONCILIATION_QUERY_TYPE.TWO.code
                ? routeType
                : RECONCILIATION_QUERY_TYPE.ONE.code
    );
    const [refreshKey, setRefreshKey] = useState(0);
    const [confirmDialog, setConfirmDialog] = useState<{
        title?: string;
        tip: string;
        action: () => Promise<unknown>;
    }>();
    const [rejectRow, setRejectRow] = useState<PurchaseReconciliation>();
    const [rejectReason, setRejectReason] = useState("");
    const permissions = (userInfo as any).permissions as number[] | undefined;
    const hasReconciliationPermission =
        account.accountType !== AccountType.SUB_ACCOUNT ||
        Boolean(permissions?.includes(PERMISSION.RECONCILIATION.code));
    const hasSignaturePermission =
        account.accountType !== AccountType.SUB_ACCOUNT || Boolean(permissions?.includes(PERMISSION.SIGNATURE.code));
    const isSupplySide = type === RECONCILIATION_QUERY_TYPE.ONE.code;
    const title = isCustomer ? "对账确认" : isSupplier ? "对账列表" : undefined;
    const reload = () => setRefreshKey(value => value + 1);
    /** 旧版签章接口返回 ticket 时打开签署页，否则提示等待印章使用人。 */
    const openTicket = async (action: () => Promise<any>) => {
        const data = await action();
        if (data?.errors || data?.status === 400) return;
        if (!data?.ticket) {
            void message.success("已发起签署，请等待专人进行签署");
        } else {
            window.open(data.ticket);
        }
        reload();
    };
    const confirm = (tip: string, action: () => Promise<unknown>, title?: string) =>
        setConfirmDialog({ title, tip, action });
    const reject = (row: PurchaseReconciliation) => {
        setRejectReason("");
        setRejectRow(row);
    };
    /** 依据旧 analysisState 规则判断当前代理身份是否已经签署。 */
    const signedSelf = (row: PurchaseReconciliation, invalid = false) => {
        const signState = invalid ? row.invalidSignState : Number((row as any).sigingState ?? row.state) & 0xffff;
        if (signState === SIGN_STATE.ALL_SIGN.code) return true;
        if (isCustomer) return signState === SIGN_STATE.BUYER_SIGN.code;
        if (isSupplier) return signState === SIGN_STATE.SELLER_SIGN.code;
        return String(row.supplierCustomerId) === String(account.proxyActualAccount.id)
            ? signState === SIGN_STATE.SELLER_SIGN.code
            : signState === SIGN_STATE.BUYER_SIGN.code;
    };
    const sealUser = (row: PurchaseReconciliation) => {
        const proxyIsBuyer = String(account.proxyActualAccount.id) === String(row.purchaseCustomerId);
        const signId = proxyIsBuyer ? (row.purchaseEnterprise as any)?.signId : (row.supplierEnterprise as any)?.signId;
        return String(account.actualAccount.id) === String(signId);
    };
    const operation = (row: PurchaseReconciliation) => {
        const buttons: ReactNode[] = [];
        if (!isSupplySide) {
            if (row.state === RECONCILIATION_STATE.SIGNING.code && !signedSelf(row) && sealUser(row))
                buttons.push(
                    <Button
                        key="sign"
                        type="link"
                        onClick={() => void openTicket(() => purchaseReconciliationService.signing(row.id))}
                    >
                        签署
                    </Button>
                );
            if (
                row.purchaseCustomerId === (userInfo.customer as any)?.id &&
                row.state === RECONCILIATION_STATE.SIGNING.code &&
                hasReconciliationPermission
            )
                buttons.push(
                    <Button key="reject" type="link" onClick={() => reject(row)}>
                        驳回
                    </Button>
                );
        } else {
            if (row.state === RECONCILIATION_STATE.DRAFT.code && hasReconciliationPermission)
                buttons.push(
                    <Button
                        key="start"
                        type="link"
                        onClick={() => void openTicket(() => purchaseReconciliationService.initiateSigning(row.id))}
                    >
                        发起签署
                    </Button>
                );
            if (row.state === RECONCILIATION_STATE.SIGNING.code && !signedSelf(row) && sealUser(row))
                buttons.push(
                    <Button
                        key="sign"
                        type="link"
                        onClick={() => void openTicket(() => purchaseReconciliationService.signing(row.id))}
                    >
                        签署
                    </Button>
                );
            if (
                [RECONCILIATION_STATE.DRAFT.code, RECONCILIATION_STATE.REJECTED.code].includes(row.state) &&
                hasReconciliationPermission
            ) {
                buttons.push(
                    <Button
                        key="edit"
                        type="link"
                        onClick={() => navigate(`/console/reconciliation/form?id=${row.id}&typeTabsActive=${type}`)}
                    >
                        修改
                    </Button>
                );
                buttons.push(
                    <Button
                        key="delete"
                        danger
                        type="link"
                        onClick={() => confirm("是否删除", () => purchaseReconciliationService.delete(row.id))}
                    >
                        删除
                    </Button>
                );
            }
            if (
                row.state === RECONCILIATION_STATE.FINISHED.code &&
                row.invoiceState === PURCHASE_RECONCILIATION_INVOICE_STATE.NOT_INVOICED.code &&
                hasReconciliationPermission
            )
                buttons.push(
                    <Button
                        key="invalid"
                        type="link"
                        onClick={() =>
                            confirm(
                                "作废后，需双方签署作废对账单后才作废，是否发起作废？",
                                () => openTicket(() => purchaseReconciliationService.startInvalid(Number(row.id))),
                                "已发起作废"
                            )
                        }
                    >
                        发起作废
                    </Button>
                );
        }
        if (row.state === RECONCILIATION_STATE.INVALIDING.code && !signedSelf(row, true) && sealUser(row))
            buttons.push(
                <Button
                    key="invalid-sign"
                    type="link"
                    onClick={() => void openTicket(() => purchaseReconciliationService.signing(row.id))}
                >
                    签署作废文件
                </Button>
            );
        return buttons.length ? <Space className="reconciliation-operation">{buttons}</Space> : "--";
    };
    const enterpriseName = (enterprise?: PurchaseReconciliation["purchaseEnterprise"]) =>
        enterprise?.name || (enterprise as any)?.legalRepresentative || (enterprise as any)?.realName || "--";
    const columns = useMemo<ProColumn<PurchaseReconciliation>[]>(
        () => [
            {
                key: "id",
                title: "编号",
                width: 150,
                search: { key: "param", type: "input", placeholder: "请输入编号或项目名称", span: 6, order: 4 },
                render: (_, row) => (
                    <Button
                        className="reconciliation-code-link"
                        type="link"
                        onClick={() => navigate(`/console/reconciliation/detail?id=${row.id}&type=${type}`)}
                    >
                        {row.id}
                    </Button>
                )
            },
            { key: "projectName", title: "项目", minWidth: 142 },
            {
                key: "enterprise",
                title: isSupplySide ? "对账单位" : "供货单位",
                minWidth: 142,
                search: {
                    key: "name",
                    type: "input",
                    placeholder: isSupplySide ? "请输入对账单位名称" : "请输入供货单位名称",
                    order: 5
                },
                render: (_, row) => enterpriseName(isSupplySide ? row.purchaseEnterprise : row.supplierEnterprise)
            },
            {
                key: "reconciliationDate",
                title: "对账日期",
                minWidth: 180,
                search: { key: "reconciliationDate", type: "dateRange", placeholder: "日期范围选择", order: 3 },
                render: value => dateFormat(value as string)
            },
            { key: "actualAmount", title: "折扣金额（元）", minWidth: 142 },
            {
                key: "state",
                title: "对账状态",
                minWidth: 86,
                enum: reconciliationStateOptions,
                search: { key: "state", type: "select", placeholder: "请选择对账状态", options: reconciliationStateOptions, order: 1 }
            },
            { key: "operation", title: "操作", width: isSupplySide ? 194 : 120, fixed: "right", render: (_, row) => operation(row) }
        ],
        [hasReconciliationPermission, isSupplySide, navigate, type]
    );
    return (
        <section className="reconciliation-list-page section-card">
            <Breadcrumb
                routes={[{ name: "采购" }, { name: "对账列表" }]}
            />
            {isSupplyChain ? (
                <Tabs
                    className="reconciliation-role-tabs"
                    activeKey={String(type)}
                    onChange={key => setType(Number(key))}
                    items={[
                        { key: String(RECONCILIATION_QUERY_TYPE.ONE.code), label: "对账下游" },
                        { key: String(RECONCILIATION_QUERY_TYPE.TWO.code), label: "供应商对账确认" }
                    ]}
                />
            ) : null}
            <div className="reconciliation-list-table">
                <ProTable
                    key={`${type}-${refreshKey}`}
                    title={title}
                    rowKey="id"
                    columns={columns}
                    initialParams={{ type }}
                    request={async params =>
                        purchaseReconciliationService.paging({ ...params, type } as never) as never
                    }
                    header={
                        isSupplySide && hasSignaturePermission
                            ? () => (
                                  <Button
                                      type="primary"
                                      onClick={() => navigate(`/console/reconciliation/form?typeTabsActive=${type}`)}
                                  >
                                      新增
                                  </Button>
                              )
                            : undefined
                    }
                />
            </div>
            <ZhMessageBox
                open={Boolean(confirmDialog)}
                title={confirmDialog?.title}
                tip={confirmDialog?.tip}
                onCancel={() => setConfirmDialog(undefined)}
                onSubmit={async () => {
                    if (!confirmDialog) return;
                    await confirmDialog.action();
                    setConfirmDialog(undefined);
                    reload();
                }}
            />
            <ZhMessageBox
                open={Boolean(rejectRow)}
                title="驳回"
                tip={
                    <div className="reconciliation-reject-box">
                        <p>是否驳回，驳回后该对账单将不再展示</p>
                        <Input.TextArea
                            value={rejectReason}
                            placeholder="请输入驳回理由"
                            onChange={event => setRejectReason(event.target.value)}
                        />
                    </div>
                }
                onCancel={() => setRejectRow(undefined)}
                onSubmit={async () => {
                    if (!rejectRow || !rejectReason.trim()) {
                        void message.error("请输入驳回理由");
                        return;
                    }
                    await purchaseReconciliationService.reject(rejectRow.id, rejectReason.trim());
                    setRejectRow(undefined);
                    reload();
                }}
            />
        </section>
    );
}

interface FormValues {
    projectId: number;
    purchaseCustomerId?: number;
    reconciliationDate: dayjs.Dayjs;
    actualAmount: number;
    remark?: string;
}
interface ReceiptShipRow {
    key: string;
    receiptIndex: number;
    goodsIndex: number;
    shipIndex: number;
    receipt: PurchaseGoodsReceiptVo["purchaseGoodsReceipt"];
    goods: PurchaseGoodsReceiptVo["purchaseGoodsList"][number];
    ship: PurchaseGoodsReceiptVo["purchaseGoodsList"][number]["ships"][number];
}
/** 对账表单对应旧 remote-select、关联弹窗、签收明细表和底部固定操作区。 */
export function ReconciliationFormPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const routeProjectId = Number(params.get("projectId"));
    const typeTabsActive = Number(params.get("typeTabsActive") || RECONCILIATION_QUERY_TYPE.ONE.code);
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const proxyAccountId = useAccountSession(state => state.proxyActualAccount.id);
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const [form] = Form.useForm<FormValues>();
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project>();
    const [buyers, setBuyers] = useState<ProjectExtend[]>([]);
    const [receiptOptions, setReceiptOptions] = useState<PurchaseGoodsReceiptVo[]>([]);
    const [selectedReceipts, setSelectedReceipts] = useState<PurchaseGoodsReceiptVo[]>([]);
    const [checkedIds, setCheckedIds] = useState<string[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitPayload, setSubmitPayload] = useState<PurchaseReconciliationForm>();
    const purchaseCustomerId = Form.useWatch("purchaseCustomerId", form);
    const buyerSelectable = isSupplyChain && !id;
    const reconciliationType = useMemo(() => {
        if (!selectedProject) return 0;
        if (selectedProject.purType === PURCHASE_TYPE.DIRECT.code) return 1;
        if (isCustomer || (isSupplyChain && typeTabsActive === RECONCILIATION_QUERY_TYPE.ONE.code)) return 3;
        return 2;
    }, [isCustomer, isSupplyChain, selectedProject, typeTabsActive]);
    const buyerOptions = buyers.map(item => ({
        label: item.buyerEnterprise?.name || item.buyerEnterprise?.legalRepresentative || "--",
        value: item.buyerId
    }));
    const selectedBuyer = buyers.find(item => item.buyerId === purchaseCustomerId);
    const provider =
        reconciliationType === 3 ? selectedProject?.supplyChainEnterprise : selectedProject?.supplierEnterprise;
    const consumer = reconciliationType === 2 ? selectedProject?.supplyChainEnterprise : selectedBuyer?.buyerEnterprise;
    /** 切换项目时同步双方信息，并清空不再属于该项目的签收单。 */
    const selectProject = async (project: Project) => {
        setSelectedProject(project);
        setSelectedReceipts([]);
        setCheckedIds([]);
        const projectBuyers = await projectService.getBuyers(project.id);
        setBuyers(projectBuyers);
        const purchaseCustomerId =
            isSupplier && project.purType === PURCHASE_TYPE.JOINT.code
                ? project.supplyChainId
                : isCustomer
                  ? (projectBuyers.find(item => item.buyerId === proxyAccountId)?.buyerId ?? projectBuyers[0]?.buyerId)
                  : projectBuyers[0]?.buyerId;
        form.setFieldValue("purchaseCustomerId", purchaseCustomerId);
    };
    useEffect(() => {
        void projectService.noPaging({ states: PROJECT_STATE.PROCESSING.code.toString() } as never).then(setProjects);
    }, []);
    useEffect(() => {
        const initialize = async () => {
            if (id) {
                const info = await purchaseReconciliationService.getInfo(id);
                setSelectedProject(info.project as unknown as Project);
                setSelectedReceipts(info.purchaseGoodsReceiptVos as unknown as PurchaseGoodsReceiptVo[]);
                setCheckedIds(info.purchaseGoodsReceiptVos.map(item => item.purchaseGoodsReceipt.id));
                const projectBuyers = await projectService.getBuyers(info.project.id);
                setBuyers(projectBuyers);
                form.setFieldsValue({
                    projectId: info.project.id,
                    purchaseCustomerId: info.purchaseReconciliation.purchaseCustomerId,
                    reconciliationDate: dayjs(info.purchaseReconciliation.reconciliationDate),
                    actualAmount: info.purchaseReconciliation.actualAmount,
                    remark: info.purchaseReconciliation.remark
                });
            } else if (routeProjectId) {
                const projectInfo = await projectService.getInfo(routeProjectId);
                form.setFieldValue("projectId", routeProjectId);
                await selectProject(projectInfo.project);
            }
        };
        void initialize().catch(() => void message.error("加载失败"));
    }, [id, routeProjectId]);
    const openAssociateDialog = async () => {
        const projectId = form.getFieldValue("projectId");
        const purchaseCustomerId = form.getFieldValue("purchaseCustomerId");
        if (!projectId) {
            await form.validateFields(["projectId"]);
            return;
        }
        if (!purchaseCustomerId) return;
        const options = await purchaseGoodsReceiptService.findByProjectId(projectId, purchaseCustomerId);
        setReceiptOptions(options);
        setCheckedIds(selectedReceipts.map(item => item.purchaseGoodsReceipt.id));
        setDialogOpen(true);
    };
    const receiptRows = useMemo<ReceiptShipRow[]>(
        () =>
            selectedReceipts.flatMap((item, receiptIndex) =>
                item.purchaseGoodsList.flatMap((goods, goodsIndex) =>
                    goods.ships.map((ship, shipIndex) => ({
                        key: `${item.purchaseGoodsReceipt.id}-${goods.id}-${shipIndex}`,
                        receiptIndex,
                        goodsIndex,
                        shipIndex,
                        receipt: item.purchaseGoodsReceipt,
                        goods,
                        ship
                    }))
                )
            ),
        [selectedReceipts]
    );
    const quantity = selectedReceipts.reduce((sum, item) => sum + Number(item.purchaseGoodsReceipt.quantity || 0), 0);
    const calculatedAmount = Number(
        receiptRows
            .reduce((sum, row) => sum + Number(row.ship.unitPrice || 0) * Number(row.ship.receiptQuantity || 0), 0)
            .toFixed(2)
    );
    /** 单价直接回写所选签收单的船舶对象，提交结构与旧版 purchaseGoodsItems 一致。 */
    const updateUnitPrice = (row: ReceiptShipRow, value: number | null) => {
        setSelectedReceipts(items =>
            items.map((item, receiptIndex) => ({
                ...item,
                purchaseGoodsList: item.purchaseGoodsList.map((goods, goodsIndex) => ({
                    ...goods,
                    ships: goods.ships.map((ship, shipIndex) =>
                        receiptIndex === row.receiptIndex &&
                        goodsIndex === row.goodsIndex &&
                        shipIndex === row.shipIndex
                            ? { ...ship, unitPrice: Number(value || 0) }
                            : ship
                    )
                }))
            }))
        );
    };
    const receiptColumns = useMemo<ProColumn<ReceiptShipRow>[]>(
        () => [
            {
                key: "receiptId",
                title: "签收编号",
                width: 150,
                render: (_, row) => (
                    <Button
                        className="reconciliation-code-link"
                        type="link"
                        onClick={() => navigate(`/console/purchaseGoodsReceipt/detail?id=${row.receipt.id}`)}
                    >
                        {row.receipt.id}
                    </Button>
                )
            },
            {
                key: "confirmedDate",
                title: "确认日期",
                render: (_, row) => dateFormat(row.receipt.receiptConfirmedDate)
            },
            { key: "shipName", title: "船名", render: (_, row) => row.ship.cnName || row.ship.name || "--" },
            { key: "receiptDate", title: "签收日期", render: (_, row) => dateFormat(row.ship.receiptDate) },
            { key: "receiptQuantity", title: "签收吨数", render: (_, row) => formatNumber(row.ship.receiptQuantity) },
            {
                key: "unitPrice",
                title: "单价(元/吨)",
                width: 160,
                render: (_, row) => (
                    <InputNumber
                        min={1}
                        precision={2}
                        placeholder="请输入签收单价"
                        value={row.ship.unitPrice || undefined}
                        onChange={value => updateUnitPrice(row, value)}
                    />
                )
            },
            {
                key: "subtotal",
                title: "小计(¥)",
                render: (_, row) =>
                    formatNumber(Number(row.ship.unitPrice || 0) * Number(row.ship.receiptQuantity || 0), 2)
            }
        ],
        [navigate]
    );
    const submit = async (values: FormValues) => {
        if (!selectedReceipts.length) {
            void message.error("请选择关联签收单");
            return;
        }
        if (receiptRows.some(row => !row.ship.unitPrice)) {
            void message.error("单价不能为空");
            return;
        }
        const payload: PurchaseReconciliationForm = {
            projectId: values.projectId,
            purchaseGoodsReceiptItems: selectedReceipts.map(item => ({ id: item.purchaseGoodsReceipt.id })),
            purchaseGoodsItems: selectedReceipts.flatMap(item =>
                item.purchaseGoodsList.map(goods => ({ id: goods.id, arrayGoodsShip: goods.ships }))
            ),
            type: reconciliationType,
            quantity,
            calculatedAmount,
            actualAmount: values.actualAmount,
            reconciliationDate: values.reconciliationDate.format("YYYY-MM-DD HH:mm:ss"),
            purchaseCustomerId: Number(values.purchaseCustomerId),
            remark: values.remark
        };
        setSubmitPayload(payload);
    };
    return (
        <section className="reconciliation-form-shell">
            <div className="reconciliation-form-page section-card">
                <Breadcrumb routes={[{ name: "采购" }, { name: id ? "修改对账" : "新增对账" }]} />
                <h1>{id ? "修改" : "新增"}对账</h1>
                <Form form={form} layout="vertical" onFinish={submit}>
                    <div className="reconciliation-form-project">
                        <Form.Item
                            name="projectId"
                            label="关联项目"
                            rules={[{ required: true, message: "请选择项目" }]}
                        >
                            <Select
                                showSearch
                                disabled={Boolean(id)}
                                optionFilterProp="label"
                                placeholder="请下拉选择或搜索项目"
                                options={projects.map(item => ({ label: item.name, value: item.id }))}
                                onSearch={name =>
                                    void projectService
                                        .noPaging({ states: PROJECT_STATE.PROCESSING.code.toString(), name } as never)
                                        .then(setProjects)
                                }
                                onChange={projectId => {
                                    const project = projects.find(item => item.id === projectId);
                                    if (project) void selectProject(project);
                                }}
                            />
                        </Form.Item>
                        {selectedProject ? (
                            <div className="reconciliation-project-summary">
                                <span>
                                    <i>品类</i>
                                    <b>{selectedProject.productTypeName || selectedProject.productName || "--"}</b>
                                </span>
                                {buyerSelectable ? (
                                    <Form.Item name="purchaseCustomerId" label="对账单位" rules={[{ required: true }]}>
                                        <Select className="buyer-selector" options={buyerOptions} />
                                    </Form.Item>
                                ) : (
                                    <span>
                                        <i>对账单位</i>
                                        <b>{consumer?.name || consumer?.legalRepresentative || "--"}</b>
                                    </span>
                                )}
                                <span>
                                    <i>供货单位</i>
                                    <b>{provider?.name || provider?.legalRepresentative || "--"}</b>
                                </span>
                            </div>
                        ) : null}
                    </div>
                    <Form.Item
                        className="reconciliation-associate-field"
                        label={
                            <span>
                                关联签收单
                                <Button
                                    className="reconciliation-associate-button"
                                    type="link"
                                    onClick={() => void openAssociateDialog()}
                                >
                                    关联 <i className="iconfont icon-Frame-3" />
                                </Button>
                            </span>
                        }
                        required
                    >
                        {receiptRows.length ? (
                            <ProTable rowKey="key" columns={receiptColumns} data={receiptRows} pagination={false} />
                        ) : null}
                        {selectedReceipts.length ? (
                            <div className="reconciliation-totals">
                                <span>
                                    <i>对账吨数</i>
                                    <b>{formatNumber(quantity)}</b>
                                </span>
                                <span>
                                    <i>核算金额</i>
                                    <b>{formatNumber(calculatedAmount, 2)}</b>
                                </span>
                            </div>
                        ) : null}
                    </Form.Item>
                    {selectedReceipts.length ? (
                        <div className="reconciliation-form-row">
                            <Form.Item
                                name="reconciliationDate"
                                label="对账日期"
                                rules={[{ required: true, message: "请选择对账日期" }]}
                            >
                                <DatePicker placeholder="请选择对账日期" />
                            </Form.Item>
                            <Form.Item
                                name="actualAmount"
                                label="折扣金额"
                                rules={[{ required: true, message: "请输入折扣金额" }]}
                            >
                                <InputNumber min={0} precision={2} placeholder="请输入折扣金额" />
                            </Form.Item>
                        </div>
                    ) : null}
                    <Form.Item name="remark" label="备注">
                        <Input.TextArea rows={3} maxLength={200} showCount placeholder="如有其他说明，请备注" />
                    </Form.Item>
                </Form>
                <Modal
                    centered
                    className="reconciliation-associate-dialog"
                    open={dialogOpen}
                    title="关联签收单"
                    width={582}
                    okText="提交"
                    cancelText="取消"
                    onCancel={() => setDialogOpen(false)}
                    onOk={() => {
                        setSelectedReceipts(
                            receiptOptions.filter(item => checkedIds.includes(item.purchaseGoodsReceipt.id))
                        );
                        setDialogOpen(false);
                    }}
                >
                    <Checkbox.Group value={checkedIds} onChange={values => setCheckedIds(values.map(String))}>
                        <div className="reconciliation-receipt-options">
                            {receiptOptions.map(item => (
                                <Checkbox
                                    className="reconciliation-receipt-option"
                                    key={item.purchaseGoodsReceipt.id}
                                    value={item.purchaseGoodsReceipt.id}
                                >
                                    <strong>{item.purchaseGoodsReceipt.id}</strong>
                                    <span>
                                        <i>吨数</i>
                                        {formatNumber(item.purchaseGoodsReceipt.quantity)}
                                    </span>
                                    <span>
                                        <i>确认日期</i>
                                        {dateFormat(item.purchaseGoodsReceipt.receiptConfirmedDate)}
                                    </span>
                                </Checkbox>
                            ))}
                            {!receiptOptions.length ? <EmptyState /> : null}
                        </div>
                    </Checkbox.Group>
                </Modal>
            </div>
            <div className="operation-fixed-btn reconciliation-form-actions">
                <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                    保存为草稿
                </Button>
                <Button type="primary" onClick={() => navigate("/console/reconciliation")}>
                    返回
                </Button>
            </div>
            <ZhMessageBox
                open={Boolean(submitPayload)}
                tip="是否提交"
                onCancel={() => setSubmitPayload(undefined)}
                onSubmit={async () => {
                    if (!submitPayload) return;
                    setSubmitting(true);
                    try {
                        if (id) await purchaseReconciliationService.update(id, submitPayload);
                        else await purchaseReconciliationService.create(submitPayload);
                        setSubmitPayload(undefined);
                        navigate(-1);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            />
        </section>
    );
}

/** 对账详情逐项对应旧 DetailHeader、InfoCard、BaseInfo、签收表格和状态操作。 */
export function ReconciliationDetailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const type = Number(params.get("type") || RECONCILIATION_QUERY_TYPE.ONE.code);
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const userInfo = useUserSession(state => state.userInfo);
    const account = useAccountSession();
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const permissions = (userInfo as any).permissions as number[] | undefined;
    const hasPermission =
        account.accountType !== AccountType.SUB_ACCOUNT ||
        Boolean(permissions?.includes(PERMISSION.RECONCILIATION.code));
    const [info, setInfo] = useState<PurchaseReconciliationVo>();
    const [confirmDialog, setConfirmDialog] = useState<{
        title?: string;
        tip: string;
        action: () => Promise<unknown>;
    }>();
    const [rejecting, setRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const load = async () => {
        if (!id) return;
        try {
            setInfo(await purchaseReconciliationService.getInfo(id));
        } catch {
            void message.error("加载失败");
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    if (!info) return null;
    const reconciliation = info.purchaseReconciliation;
    const receiptRows = info.purchaseGoodsReceiptVos.flatMap((item, receiptIndex) =>
        item.purchaseGoodsList.flatMap((goods, goodsIndex) =>
            goods.ships.map((ship, shipIndex) => ({
                key: `${item.purchaseGoodsReceipt.id}-${goods.id}-${shipIndex}`,
                receiptIndex,
                goodsIndex,
                shipIndex,
                receipt: item.purchaseGoodsReceipt,
                goods,
                ship
            }))
        )
    ) as unknown as ReceiptShipRow[];
    const receiptColumns: ProColumn<ReceiptShipRow>[] = [
        {
            key: "receiptId",
            title: "签收编号",
            width: 150,
            render: (_, row) => (
                <Button
                    className="reconciliation-code-link"
                    type="link"
                    onClick={() => navigate(`/console/purchaseGoodsReceipt/detail?id=${row.receipt.id}`)}
                >
                    {row.receipt.id}
                </Button>
            )
        },
        { key: "confirmedDate", title: "确认日期", render: (_, row) => dateFormat(row.receipt.receiptConfirmedDate) },
        { key: "shipName", title: "船名", render: (_, row) => row.ship.cnName || row.ship.name || "--" },
        { key: "receiptDate", title: "签收日期", render: (_, row) => dateFormat(row.ship.receiptDate) },
        { key: "receiptQuantity", title: "签收吨数", render: (_, row) => formatNumber(row.ship.receiptQuantity) },
        { key: "unitPrice", title: "单价(元/吨)", render: (_, row) => formatNumber(row.ship.unitPrice, 2) },
        {
            key: "subtotal",
            title: "小计(¥)",
            render: (_, row) => formatNumber(Number(row.ship.unitPrice || 0) * Number(row.ship.receiptQuantity || 0), 2)
        }
    ];
    /** 复合签署状态低位与旧 analysisState/getSelfSignState 的角色判断一致。 */
    const signedSelf = (invalid = false) => {
        const signState = invalid
            ? reconciliation.invalidSignState
            : Number((reconciliation as any).sigingState ?? reconciliation.state) & 0xffff;
        if (signState === SIGN_STATE.ALL_SIGN.code) return true;
        if (isCustomer) return signState === SIGN_STATE.BUYER_SIGN.code;
        if (isSupplier) return signState === SIGN_STATE.SELLER_SIGN.code;
        return String(reconciliation.supplierCustomerId) === String(account.proxyActualAccount.id)
            ? signState === SIGN_STATE.SELLER_SIGN.code
            : signState === SIGN_STATE.BUYER_SIGN.code;
    };
    const sealUser = () => {
        const proxyIsBuyer = String(account.proxyActualAccount.id) === String(reconciliation.purchaseCustomerId);
        const signId = proxyIsBuyer
            ? (reconciliation.purchaseEnterprise as any)?.signId
            : (reconciliation.supplierEnterprise as any)?.signId;
        return String(account.actualAccount.id) === String(signId);
    };
    const openTicket = async (action: () => Promise<any>) => {
        const data = await action();
        if (data?.errors || data?.status === 400) return;
        if (data?.ticket) window.open(data.ticket);
        else void message.success("已发起签署，请等待专人进行签署");
        await load();
    };
    const confirmAction = (tip: string, action: () => Promise<unknown>, title?: string) =>
        setConfirmDialog({ title, tip, action });
    const reject = () => {
        setRejectReason("");
        setRejecting(true);
    };
    const actions: DetailAction<PurchaseReconciliation>[] = [];
    if (type === RECONCILIATION_QUERY_TYPE.TWO.code) {
        actions.push(
            {
                label: "签署",
                visible: row => row.state === RECONCILIATION_STATE.SIGNING.code && !signedSelf() && sealUser(),
                action: row => openTicket(() => purchaseReconciliationService.signing(row.id))
            },
            {
                label: "驳回",
                visible: row =>
                    row.purchaseCustomerId === (userInfo.customer as any)?.id &&
                    row.state === RECONCILIATION_STATE.SIGNING.code &&
                    hasPermission,
                action: () => reject()
            }
        );
    } else {
        actions.push(
            {
                label: "发起签署",
                visible: row => row.state === RECONCILIATION_STATE.DRAFT.code && hasPermission,
                action: row => openTicket(() => purchaseReconciliationService.initiateSigning(row.id))
            },
            {
                label: "签署",
                visible: row => row.state === RECONCILIATION_STATE.SIGNING.code && !signedSelf() && sealUser(),
                action: row => openTicket(() => purchaseReconciliationService.signing(row.id))
            },
            {
                label: "修改",
                visible: row =>
                    [RECONCILIATION_STATE.DRAFT.code, RECONCILIATION_STATE.REJECTED.code].includes(row.state) &&
                    hasPermission,
                action: row => navigate(`/console/reconciliation/form?id=${row.id}&typeTabsActive=${type}`)
            },
            {
                label: "删除",
                visible: row =>
                    [RECONCILIATION_STATE.DRAFT.code, RECONCILIATION_STATE.REJECTED.code].includes(row.state) &&
                    hasPermission,
                action: row => {
                    confirmAction("是否删除", async () => {
                        await purchaseReconciliationService.delete(row.id);
                        navigate("/console/reconciliation");
                    });
                }
            },
            {
                label: "发起作废",
                visible: row =>
                    row.state === RECONCILIATION_STATE.FINISHED.code &&
                    row.invoiceState === PURCHASE_RECONCILIATION_INVOICE_STATE.NOT_INVOICED.code &&
                    hasPermission,
                action: row => {
                    confirmAction(
                        "作废后，需双方签署作废对账单后才作废，是否发起作废？",
                        () => openTicket(() => purchaseReconciliationService.startInvalid(Number(row.id))),
                        "已发起作废"
                    );
                }
            }
        );
    }
    actions.push(
        {
            label: "签署作废文件",
            visible: row => row.state === RECONCILIATION_STATE.INVALIDING.code && !signedSelf(true) && sealUser(),
            action: row => openTicket(() => purchaseReconciliationService.signing(row.id))
        },
        { label: "返回", action: () => navigate("/console/reconciliation") }
    );
    return (
        <section className="reconciliation-detail-page detail-container">
            <DetailHeader
                title="对账确认详情"
                menu={<Breadcrumb routes={[{ name: "采购" }, { name: "对账列表", path: "/console/reconciliation" }, { name: "对账详情" }]} />}
                leftInfos={[{ label: "编号", value: reconciliation.id }]}
                rightInfos={[{ label: "更新时间", value: dateTimeFormat(reconciliation.updatedTime) }]}
                state={{
                    text: PURCHASE_RECONCILIATION_RECONCILIATION_STATE.getIntroByCode(reconciliation.state) ?? undefined
                }}
            />
            <InfoCard title="基本信息">
                <ZhGrid cols={4}>
                    <ZhBaseItem label="项目" span={2}>
                        <Button
                            className="reconciliation-code-link"
                            type="link"
                            onClick={() => navigate(`/console/project/detail?id=${info.project.id}`)}
                        >
                            {info.project.name || "--"}
                        </Button>
                    </ZhBaseItem>
                    <ZhBaseItem
                        label="品类"
                        value={info.project.productTypeName || reconciliation.productTypeName}
                        span={2}
                    />
                    <ZhBaseItem label="供货单位" value={reconciliation.supplierEnterprise?.name} />
                    <ZhBaseItem
                        label="对账单位"
                        value={
                            reconciliation.purchaseEnterprise?.name ||
                            (reconciliation.purchaseEnterprise as any)?.legalRepresentative
                        }
                    />
                    <ZhBaseItem label="对账吨数" value={formatNumber(reconciliation.quantity)} />
                    <ZhBaseItem label="对账日期" value={dateFormat(reconciliation.reconciliationDate)} />
                    <ZhBaseItem label="核算金额" value={`¥${formatNumber(reconciliation.calculatedAmount, 2)}`} />
                    <ZhBaseItem label="折扣金额" value={`¥${formatNumber(reconciliation.actualAmount, 2)}`} />
                    <ZhBaseItem label="是否已开票" value={INVOICE_STATE.getIntroByCode(reconciliation.invoiceState)} />
                    <ZhBaseItem label="单据" span={4}>
                        {info.file ? (
                            <>
                                <span>{info.file.name}</span>
                                <a href={info.file.path} target="_blank" rel="noreferrer">
                                    查看/下载
                                </a>
                            </>
                        ) : (
                            "--"
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="备注" value={reconciliation.remark} span={4} />
                    {reconciliation.state === RECONCILIATION_STATE.REJECTED.code && reconciliation.reason ? (
                        <ZhBaseItem label="驳回原因" value={reconciliation.reason} span={4} />
                    ) : null}
                </ZhGrid>
            </InfoCard>
            {info.purchaseGoodsReceiptVos ? (
                <InfoCard title="签收单">
                    <div className="reconciliation-detail-receipts">
                        <ProTable rowKey="key" columns={receiptColumns} data={receiptRows} pagination={false} />
                    </div>
                </InfoCard>
            ) : null}
            {[RECONCILIATION_STATE.INVALIDING.code, RECONCILIATION_STATE.INVALID.code].includes(
                reconciliation.state
            ) ? (
                <InfoCard title="作废信息">
                    <ZhGrid cols={5}>
                        <ZhBaseItem label="文件" fileId={reconciliation.invalidFileId} />
                        {reconciliation.state === RECONCILIATION_STATE.INVALID.code ? (
                            <ZhBaseItem label="时间" value={dateTimeFormat(reconciliation.invalidTime)} />
                        ) : null}
                    </ZhGrid>
                </InfoCard>
            ) : null}
            <DetailButtons actions={actions} entity={reconciliation} />
            <ZhMessageBox
                open={Boolean(confirmDialog)}
                title={confirmDialog?.title}
                tip={confirmDialog?.tip}
                onCancel={() => setConfirmDialog(undefined)}
                onSubmit={async () => {
                    if (!confirmDialog) return;
                    await confirmDialog.action();
                    setConfirmDialog(undefined);
                    await load();
                }}
            />
            <ZhMessageBox
                open={rejecting}
                title="驳回"
                tip={
                    <div className="reconciliation-reject-box">
                        <p>是否驳回，驳回后该对账单将不再展示</p>
                        <Input.TextArea
                            value={rejectReason}
                            placeholder="请输入驳回理由"
                            onChange={event => setRejectReason(event.target.value)}
                        />
                    </div>
                }
                onCancel={() => setRejecting(false)}
                onSubmit={async () => {
                    if (!rejectReason.trim()) {
                        void message.error("请输入驳回理由");
                        return;
                    }
                    await purchaseReconciliationService.reject(reconciliation.id, rejectReason.trim());
                    setRejecting(false);
                    navigate("/console/reconciliation");
                }}
            />
        </section>
    );
}
